import {
  DetectReceiptFraudRequest,
  DetectReceiptFraudResponse,
  DetectAbnormalLayoutRequest,
  DetectAbnormalLayoutResponse,
  InvalidRequestErrorResponse,
  GetMerchantSamplesResponse,
} from './types';
import { config, endpoints } from './config';

export class ApiError extends Error {
  constructor(public status: number, message?: string, public detail?: any) {
    const messageText = message || detail?.[0].msg || 'Unknown error';
    const formatted_message = `${status} ${messageText}`;
    super(formatted_message);
  }
}

function getHeaders(authToken?: string): HeadersInit {
  const headers: HeadersInit = {};
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  return headers;
}

async function handleErrorResponse(response: Response, fallbackMessage: string): Promise<never> {
  try {
    const errorData: InvalidRequestErrorResponse = await response.json();
    throw new ApiError(response.status, errorData.message, errorData.detail);
  } catch (parseError) {
    if (parseError instanceof ApiError) {
      // If JSON parsing fails, it might be a non-JSON error response
      throw parseError;
    }
    // If JSON parsing fails, use fallback message
    throw new ApiError(response.status, fallbackMessage);
  }
}

export async function ping(authToken?: string): Promise<{ result: string }> {
  const response = await fetch(`${config.apiBase}${endpoints.ping}`, {
    headers: getHeaders(authToken),
  });
  
  if (!response.ok) {
    await handleErrorResponse(response, 'Ping failed');
  }
  
  return response.json();
}

export async function detectReceiptFraud(
  receiptImage: File,
  request: DetectReceiptFraudRequest,
  authToken?: string
): Promise<DetectReceiptFraudResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.processingTimeout);

  try {
    const formData = new FormData();
    formData.append('receipt_image', receiptImage);
    formData.append('llm_model_provider', request.llm_model_provider);
    
    if (request.llm_model_name) {
      formData.append('llm_model_name', request.llm_model_name);
    }
    
    if (request.openrouter_api_key) {
      formData.append('openrouter_api_key', request.openrouter_api_key);
    }
    
    if (request.chat_complete_request_args) {
      formData.append('chat_complete_request_args', JSON.stringify(request.chat_complete_request_args));
    }
    
    // Send custom_instructions to override backend default
    // If null, send empty string to indicate "no custom instructions"
    if (request.custom_instructions !== undefined) {
      formData.append('custom_instructions', request.custom_instructions || '');
    }
    
    if (request.max_allowed_tool_calls != null) {
      formData.append('max_allowed_tool_calls', String(request.max_allowed_tool_calls));
    }

    // Convert extracted_data object to JSON string
    formData.append('extracted_data', JSON.stringify(request.extracted_data));

    const response = await fetch(`${config.apiBase}${endpoints.detectReceiptFraud}`, {
      method: 'POST',
      body: formData,
      headers: getHeaders(authToken),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      await handleErrorResponse(response, 'Failed to detect receipt fraud');
    }
    
    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, 'Processing timeout - fraud detection took too long');
    }
    throw error;
  }
}

export async function detectAbnormalLayout(
  inputImage: File,
  request: DetectAbnormalLayoutRequest,
  referenceImages: File[] | null,
  authToken?: string
): Promise<DetectAbnormalLayoutResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.processingTimeout);

  try {
    const formData = new FormData();
    formData.append('input_image', inputImage);
    if (request.merchant_name) {
      formData.append('merchant_name', request.merchant_name);
    }
    formData.append('llm_model_provider', request.llm_model_provider);
    
    if (request.llm_model_name) {
      formData.append('llm_model_name', request.llm_model_name);
    }
    
    if (request.openrouter_api_key) {
      formData.append('openrouter_api_key', request.openrouter_api_key);
    }
    
    if (request.chat_complete_request_args) {
      formData.append('chat_complete_request_args', JSON.stringify(request.chat_complete_request_args));
    }
    
    // Send custom_instructions to override backend default
    // If null, send empty string to indicate "no custom instructions"
    if (request.custom_instructions !== undefined) {
      formData.append('custom_instructions', request.custom_instructions || '');
    }
    
    // Append reference images if provided
    if (referenceImages && referenceImages.length > 0) {
      referenceImages.forEach((file) => {
        formData.append('reference_images', file);
      });
    }

    const response = await fetch(`${config.apiBase}${endpoints.detectAbnormalLayout}`, {
      method: 'POST',
      body: formData,
      headers: getHeaders(authToken),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      await handleErrorResponse(response, 'Failed to detect abnormal layout');
    }
    
    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, 'Processing timeout - abnormal layout detection took too long');
    }
    throw error;
  }
}

export async function getMerchantSamples(
  merchantName: string,
  authToken?: string
): Promise<GetMerchantSamplesResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.processingTimeout);

  try {
    const formData = new FormData();
    formData.append('merchant_name', merchantName);

    const response = await fetch(`${config.apiBase}${endpoints.getAbnormalLayoutMerchantSamples}`, {
      method: 'POST',
      body: formData,
      headers: getHeaders(authToken),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      await handleErrorResponse(response, 'Failed to get merchant samples');
    }
    
    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, 'Request timeout - getting merchant samples took too long');
    }
    throw error;
  }
}
